import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { ToastService } from '../../../services/toast-service';
import { SystemSettingsService, SystemConfiguration } from './system-settings.service';

interface SettingField {
    key: string;
    labelKey: string;
    type: string;        // 'text' | 'password' | 'url' | 'boolean'
    configType: string;  // Maps to SystemConfiguration.ConfigType
    category: string;
    required: boolean;
    placeholderKey?: string;
    helpKey?: string;
    order: number;
}

@Component({
    selector: 'app-system-settings',
    templateUrl: './system-settings.component.html',
    styleUrls: ['./system-settings.component.scss']
})
export class SystemSettingsComponent implements OnInit {
    // --- Freshdesk ---
    freshdeskForm!: FormGroup;
    freshdeskLoading = false;
    freshdeskSaving = false;
    freshdeskTesting = false;
    freshdeskTestResult: { success: boolean; message: string } | null = null;

    // --- Accounting / Økonomisystem ---
    accountingForm!: FormGroup;
    accountingLoading = false;
    accountingSaving = false;

    accountingFields: SettingField[] = [
        {
            key: 'Accounting.DefaultVoucherSeries',
            labelKey: 'SystemAdmin.settings.accountingVoucherSeries',
            type: 'number',
            configType: 'Number',
            category: 'Accounting',
            required: false,
            placeholderKey: 'SystemAdmin.settings.accountingVoucherSeriesPlaceholder',
            helpKey: 'SystemAdmin.settings.accountingVoucherSeriesHelp',
            order: 1
        },
        {
            key: 'Accounting.TimeMoveDebitAccount',
            labelKey: 'SystemAdmin.settings.timeMoveDebitAccount',
            type: 'number',
            configType: 'String',
            category: 'Accounting',
            required: false,
            placeholderKey: 'SystemAdmin.settings.timeMoveDebitAccountPlaceholder',
            helpKey: 'SystemAdmin.settings.timeMoveDebitAccountHelp',
            order: 2
        },
        {
            key: 'Accounting.TimeMoveCreditAccount',
            labelKey: 'SystemAdmin.settings.timeMoveCreditAccount',
            type: 'number',
            configType: 'String',
            category: 'Accounting',
            required: false,
            placeholderKey: 'SystemAdmin.settings.timeMoveCreditAccountPlaceholder',
            helpKey: 'SystemAdmin.settings.timeMoveCreditAccountHelp',
            order: 3
        },
        {
            key: 'Accounting.TimeMoveVoucherSerie',
            labelKey: 'SystemAdmin.settings.timeMoveVoucherSerie',
            type: 'number',
            configType: 'Int',
            category: 'Accounting',
            required: false,
            placeholderKey: 'SystemAdmin.settings.timeMoveVoucherSeriePlaceholder',
            helpKey: 'SystemAdmin.settings.timeMoveVoucherSerieHelp',
            order: 4
        }
    ];

    // --- Work Order ---
    workOrderForm!: FormGroup;
    workOrderLoading = false;
    workOrderSaving = false;

    workOrderFields: SettingField[] = [
        {
            key: 'WorkOrder.HourProductCode',
            labelKey: 'SystemAdmin.settings.hourProductCode',
            type: 'text',
            configType: 'String',
            category: 'WorkOrder',
            required: false,
            placeholderKey: 'SystemAdmin.settings.hourProductCodePlaceholder',
            helpKey: 'SystemAdmin.settings.hourProductCodeHelp',
            order: 1
        }
    ];

    freshdeskFields: SettingField[] = [
        {
            key: 'Freshdesk.ApiUrl',
            labelKey: 'SystemAdmin.settings.freshdeskApiUrl',
            type: 'url',
            configType: 'URL',
            category: 'Freshdesk',
            required: true,
            placeholderKey: 'SystemAdmin.settings.freshdeskApiUrlPlaceholder',
            helpKey: 'SystemAdmin.settings.freshdeskApiUrlHelp',
            order: 1
        },
        {
            key: 'Freshdesk.ApiKey',
            labelKey: 'SystemAdmin.settings.freshdeskApiKey',
            type: 'password',
            configType: 'Password',
            category: 'Freshdesk',
            required: true,
            placeholderKey: 'SystemAdmin.settings.freshdeskApiKeyPlaceholder',
            helpKey: 'SystemAdmin.settings.freshdeskApiKeyHelp',
            order: 2
        }
    ];

    constructor(
        private fb: FormBuilder,
        private settingsService: SystemSettingsService,
        private translateService: TranslateService,
        private toastService: ToastService
    ) {}

    ngOnInit(): void {
        this.initFreshdeskForm();
        this.loadFreshdeskSettings();
        this.initAccountingForm();
        this.loadAccountingSettings();
        this.initWorkOrderForm();
        this.loadWorkOrderSettings();
    }

    // ────────────────────────────────────────────
    //  Freshdesk
    // ────────────────────────────────────────────

    private initFreshdeskForm(): void {
        const group: any = {};
        this.freshdeskFields.forEach(field => {
            const validators = field.required ? [Validators.required] : [];
            if (field.type === 'url') {
                validators.push(Validators.pattern(/^https?:\/\/.+/));
            }
            group[field.key] = ['', validators];
        });
        this.freshdeskForm = this.fb.group(group);
    }

    loadFreshdeskSettings(): void {
        this.freshdeskLoading = true;
        this.freshdeskTestResult = null;
        this.settingsService.getByCategory('Freshdesk').subscribe({
            next: (response) => {
                if (response?.success && response.result) {
                    const configs = response.result as SystemConfiguration[];
                    configs.forEach((config: SystemConfiguration) => {
                        const control = this.freshdeskForm.controls[config.configKey.trim()];
                        if (control) {
                            if (config.configValue !== '********') {
                                control.setValue(config.configValue);
                            }
                        }
                    });
                }
                this.freshdeskLoading = false;
            },
            error: () => {
                this.toastService.show({
                    type: 'error',
                    data: [this.translateService.instant('SystemAdmin.settings.errorLoadingSettings')]
                });
                this.freshdeskLoading = false;
            }
        });
    }

    saveFreshdeskSettings(): void {
        if (this.freshdeskForm.invalid) {
            Object.keys(this.freshdeskForm.controls).forEach(key => {
                this.freshdeskForm.controls[key]?.markAsTouched();
            });
            return;
        }

        this.freshdeskSaving = true;
        const configs: SystemConfiguration[] = this.freshdeskFields
            .filter(field => {
                const value = this.freshdeskForm.controls[field.key]?.value;
                return value && value.trim() !== '' && value !== '********';
            })
            .map(field => ({
                configKey: field.key,
                configValue: this.freshdeskForm.controls[field.key]?.value || '',
                configType: field.configType,
                category: field.category,
                description: '',
                isEncrypted: field.type === 'password',
                isActive: true,
                displayOrder: field.order
            }));

        this.settingsService.saveBatch(configs).subscribe({
            next: (response) => {
                if (response?.success) {
                    this.toastService.show({
                        type: 'success',
                        data: [this.translateService.instant('SystemAdmin.settings.settingsSaved')]
                    });
                } else {
                    this.toastService.show({
                        type: 'error',
                        data: [this.translateService.instant('SystemAdmin.settings.errorSavingSettings')]
                    });
                }
                this.freshdeskSaving = false;
            },
            error: () => {
                this.toastService.show({
                    type: 'error',
                    data: [this.translateService.instant('SystemAdmin.settings.errorSavingSettings')]
                });
                this.freshdeskSaving = false;
            }
        });
    }

    testFreshdeskConnection(): void {
        this.freshdeskTesting = true;
        this.freshdeskTestResult = null;

        const apiUrl = this.freshdeskForm.controls['Freshdesk.ApiUrl']?.value?.trim();
        const apiKey = this.freshdeskForm.controls['Freshdesk.ApiKey']?.value?.trim();

        if (!apiUrl || !apiKey || apiKey === '********') {
            this.freshdeskTestResult = {
                success: false,
                message: this.translateService.instant('SystemAdmin.settings.freshdeskFillFieldsFirst')
            };
            this.freshdeskTesting = false;
            return;
        }

        this.settingsService.testFreshdeskConnection({ apiUrl, apiKey }).subscribe({
            next: (response) => {
                if (response?.success && response.result) {
                    this.freshdeskTestResult = {
                        success: true,
                        message: this.translateService.instant('SystemAdmin.settings.freshdeskConnectionSuccess')
                    };
                } else {
                    this.freshdeskTestResult = {
                        success: false,
                        message: this.translateService.instant('SystemAdmin.settings.freshdeskConnectionFailed')
                    };
                }
                this.freshdeskTesting = false;
            },
            error: () => {
                this.freshdeskTestResult = {
                    success: false,
                    message: this.translateService.instant('SystemAdmin.settings.freshdeskConnectionFailed')
                };
                this.freshdeskTesting = false;
            }
        });
    }

    // ────────────────────────────────────────────
    //  Accounting / Økonomisystem
    // ────────────────────────────────────────────

    private initAccountingForm(): void {
        const group: any = {};
        this.accountingFields.forEach(field => {
            const validators = field.required ? [Validators.required] : [];
            group[field.key] = ['', validators];
        });
        this.accountingForm = this.fb.group(group);
    }

    loadAccountingSettings(): void {
        this.accountingLoading = true;
        this.settingsService.getByCategory('Accounting').subscribe({
            next: (response) => {
                if (response?.success && response.result) {
                    const configs = response.result as SystemConfiguration[];
                    configs.forEach((config: SystemConfiguration) => {
                        const control = this.accountingForm.controls[config.configKey.trim()];
                        if (control) {
                            control.setValue(config.configValue);
                        }
                    });
                }
                this.accountingLoading = false;
            },
            error: () => {
                this.toastService.show({
                    type: 'error',
                    data: [this.translateService.instant('SystemAdmin.settings.errorLoadingSettings')]
                });
                this.accountingLoading = false;
            }
        });
    }

    saveAccountingSettings(): void {
        if (this.accountingForm.invalid) {
            Object.keys(this.accountingForm.controls).forEach(key => {
                this.accountingForm.controls[key]?.markAsTouched();
            });
            return;
        }

        this.accountingSaving = true;
        const configs: SystemConfiguration[] = this.accountingFields
            .map(field => ({
                configKey: field.key,
                configValue: this.accountingForm.controls[field.key]?.value?.toString() || '',
                configType: field.configType,
                category: field.category,
                description: '',
                isEncrypted: false,
                isActive: true,
                displayOrder: field.order
            }));

        this.settingsService.saveBatch(configs).subscribe({
            next: (response) => {
                if (response?.success) {
                    this.toastService.show({
                        type: 'success',
                        data: [this.translateService.instant('SystemAdmin.settings.settingsSaved')]
                    });
                } else {
                    this.toastService.show({
                        type: 'error',
                        data: [this.translateService.instant('SystemAdmin.settings.errorSavingSettings')]
                    });
                }
                this.accountingSaving = false;
            },
            error: () => {
                this.toastService.show({
                    type: 'error',
                    data: [this.translateService.instant('SystemAdmin.settings.errorSavingSettings')]
                });
                this.accountingSaving = false;
            }
        });
    }

    // ────────────────────────────────────────────
    //  Work Order
    // ────────────────────────────────────────────

    private initWorkOrderForm(): void {
        const group: any = {};
        this.workOrderFields.forEach(field => {
            const validators = field.required ? [Validators.required] : [];
            group[field.key] = ['', validators];
        });
        this.workOrderForm = this.fb.group(group);
    }

    loadWorkOrderSettings(): void {
        this.workOrderLoading = true;
        this.settingsService.getByCategory('WorkOrder').subscribe({
            next: (response) => {
                if (response?.success && response.result) {
                    const configs = response.result as SystemConfiguration[];
                    configs.forEach((config: SystemConfiguration) => {
                        const control = this.workOrderForm.controls[config.configKey.trim()];
                        if (control) {
                            control.setValue(config.configValue);
                        }
                    });
                }
                this.workOrderLoading = false;
            },
            error: () => {
                this.toastService.show({
                    type: 'error',
                    data: [this.translateService.instant('SystemAdmin.settings.errorLoadingSettings')]
                });
                this.workOrderLoading = false;
            }
        });
    }

    saveWorkOrderSettings(): void {
        if (this.workOrderForm.invalid) {
            Object.keys(this.workOrderForm.controls).forEach(key => {
                this.workOrderForm.controls[key]?.markAsTouched();
            });
            return;
        }

        this.workOrderSaving = true;
        const configs: SystemConfiguration[] = this.workOrderFields
            .map(field => ({
                configKey: field.key,
                configValue: this.workOrderForm.controls[field.key]?.value?.toString() || '',
                configType: field.configType,
                category: field.category,
                description: '',
                isEncrypted: false,
                isActive: true,
                displayOrder: field.order
            }));

        this.settingsService.saveBatch(configs).subscribe({
            next: (response) => {
                if (response?.success) {
                    this.toastService.show({
                        type: 'success',
                        data: [this.translateService.instant('SystemAdmin.settings.settingsSaved')]
                    });
                } else {
                    this.toastService.show({
                        type: 'error',
                        data: [this.translateService.instant('SystemAdmin.settings.errorSavingSettings')]
                    });
                }
                this.workOrderSaving = false;
            },
            error: () => {
                this.toastService.show({
                    type: 'error',
                    data: [this.translateService.instant('SystemAdmin.settings.errorSavingSettings')]
                });
                this.workOrderSaving = false;
            }
        });
    }

    // Helper to check if a form control has an error and is touched
    isFieldInvalid(form: FormGroup, fieldKey: string): boolean {
        const control = form.controls[fieldKey];
        return control ? control.invalid && control.touched : false;
    }
}
